import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { msalInstance } from '@/auth/msal';
import { saveLoginHint } from '@/auth/login-hint';
import { getRouterInstance } from '@/router/router-instance';
import { markInteractionRequired } from '@/auth/interaction-required';
import { ensureRoleScreens } from '@/auth/role-screen-access';
import { CLIENTS_QUERY_KEY, fetchUserClients } from '@/client/use-clients';
import { CLIENT_STORAGE_KEYS, type ClientInfo } from '@/client/client-store';
import { getTracker, clearTelemetrySession } from '@/telemetry/tracker';
import { LegacySpinner } from '@/ui/LegacySpinner';
import { MotifButton } from '@ey-xd/motif-wc-react';

/** Legacy `SESSION_STORAGE.REQUEST_URL`; must match `requireAuth`. */
const REQUEST_URL_KEY = 'request.url';

/**
 * Read (WITHOUT clearing) the deep link stashed by `requireAuth`. Returns only a
 * safe in-app path (single leading slash, not an auth route), else null. Used to
 * resolve the landing client for the eager role-mapping warm-up before we commit
 * to consuming the deep link.
 */
function peekRequestedUrl(): string | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(REQUEST_URL_KEY);
  } catch {
    return null;
  }
  if (!stored || !stored.startsWith('/') || stored.startsWith('//')) return null;
  if (stored === '/login' || stored.startsWith('/frameredirect')) return null;
  return stored;
}

/** Read + clear the stashed deep link (see {@link peekRequestedUrl}). */
function consumeRequestedUrl(): string | null {
  const url = peekRequestedUrl();
  try {
    sessionStorage.removeItem(REQUEST_URL_KEY);
  } catch {
    /* sessionStorage may be unavailable */
  }
  return url;
}

/**
 * The client the user will land on after login, so the eager role-mapping
 * warm-up targets the right client (legacy resolves the mapping for
 * CURRENT_CLIENTID at frame-redirect). Prefers a valid clientId in the stashed
 * deep link, then the saved `currentlySelectedClientId`, else the first client
 * (matches `useClients` default selection).
 */
function resolveLandingClientId(clients: ClientInfo[], requestedUrl: string | null): string {
  const deepLinkId = requestedUrl?.split('?')[0].split('/').filter(Boolean)[0] ?? null;
  if (deepLinkId && clients.some((c) => c.id === deepLinkId)) return deepLinkId;
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
  } catch {
    /* localStorage may be unavailable */
  }
  if (saved && clients.some((c) => c.id === saved)) return saved;
  return clients[0].id;
}

/**
 * `/frameredirect` — MSAL post-redirect handler.
 *
 * AAD redirects back here after `loginRedirect` (and inside hidden iframes
 * during silent token renewal). Mirrors the legacy
 * `FrameRedirectComponent` in `raptor-shell-ui/src/app/modules/frame-redirect/`.
 *
 * Behaviour:
 *   - If we're inside a hidden iframe (token renewal), do nothing — MSAL's
 *     iframe message handler will resolve the parent's `acquireTokenSilent`
 *     and the frame gets discarded.
 *   - Otherwise: await `handleRedirectPromise`, then route to root (the
 *     authenticated layer will resolve clientId / land on the home page).
 *
 * This route MUST NOT be guarded by `requireAuth` — at the moment it loads
 * the MSAL response is still in flight and there is no active account yet.
 */
export function FrameRedirectPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Hidden iframe used by MSAL for silent token renewal — leave it alone.
    if (window.self !== window.top) {
      return;
    }

    const loginStartTime = Date.now();
    let cancelled = false;

    // Do NOT call `handleRedirectPromise()` here — `initializeMsal()` in
    // `main.tsx` already awaited it before the React tree rendered. Calling it
    // a second time re-reads the authorization code from the URL hash and
    // attempts to redeem it again, which Azure AD rejects with AADSTS54005
    // ("OAuth2 Authorization code was already redeemed"). Instead, read the
    // active account that `initializeMsal()` already resolved.
    (async () => {
      if (cancelled) return;
      const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      if (account) {
        msalInstance.setActiveAccount(account);
        // Persist the login hint so a future fresh tab / session re-auths
        // silently (legacy `loginHint` parity).
        saveLoginHint(account);
        clearTelemetrySession();

        const duration = Date.now() - loginStartTime;
        getTracker().event('Login', { duration });
      }
      const router = getRouterInstance();

      const goToLogin = () => {
        markInteractionRequired();
        if (router) router.history.replace('/login');
        else navigate({ to: '/login', replace: true });
      };

      // Legacy parity (frame-redirect → getDefaultNavigationUrlandUserContextSetup
      // → getClientsFromRum): resolve the user's clients BEFORE entering the
      // shell, then EAGER-load the role-screen mapping for the landing client
      // (legacy applyRoleMappingForCurrentClient). Priming the query cache lets
      // the shell's `useClients` reuse the client result (no double fetch), and
      // warming the role-mapping cache means the first gated navigation is a
      // cache hit that cannot fail — the hard block therefore happens ONLY here,
      // at first login, never mid-session. Failure modes all route to the login
      // page with the sign-in button (the interaction flag stops LoginPage
      // auto-forwarding, so this cannot loop):
      //   - clients fetch error → /login
      //   - zero clients → /login (legacy soft block, not a hard logout)
      //   - role-mapping fetch error → /login
      if (account) {
        let clients: ClientInfo[];
        try {
          clients = await queryClient.fetchQuery({
            queryKey: CLIENTS_QUERY_KEY,
            queryFn: fetchUserClients,
            staleTime: 5 * 60_000,
          });
        } catch {
          if (cancelled) return;
          goToLogin();
          return;
        }
        if (cancelled) return;
        if (clients.length === 0) {
          goToLogin();
          return;
        }

        const landingClientId = resolveLandingClientId(clients, peekRequestedUrl());
        try {
          await ensureRoleScreens(landingClientId);
        } catch {
          if (cancelled) return;
          goToLogin();
          return;
        }
        if (cancelled) return;
      }

      // Resume the stashed deep link (legacy navigateToLoginRequestUrl /
      // BaseRouteGuard REQUEST_URL replay); fall back to Home.
      const requested = consumeRequestedUrl();
      if (requested && router) {
        router.history.replace(requested);
      } else {
        navigate({ to: '/', replace: true });
      }
    })().catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : String(err));
    });

    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient]);

  // Legacy `frame-redirect.component`: a centered EY three-bar loader with the
  // "Loading your session" caption while auth resolves; on failure, the error
  // message + a "Retry Login" button (legacy `redirectToLogin`).
  const retryLogin = () => {
    markInteractionRequired();
    const router = getRouterInstance();
    if (router) router.history.replace('/login');
    else navigate({ to: '/login', replace: true });
  };

  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '1rem',
        padding: '2rem',
      }}
    >
      {error ? (
        <>
          <p style={{ margin: 0 }}>{error}</p>
          <MotifButton
            variant="primary"
            type="button"
            fullWidth
            className="login-page__btn"
            onClick={retryLogin}
          >
            Retry Login
          </MotifButton>
        </>
      ) : (
        <>
          <LegacySpinner />
          <div>
            Loading your session
            <br />
            You will be redirected in a few seconds....
          </div>
        </>
      )}
    </section>
  );
}
